import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  FileText, 
  Users, 
  Download, 
  Calendar, 
  Search, 
  AlertCircle, 
  Loader,
  RefreshCw,
  Filter
} from "lucide-react";
import { API_BASE_URL } from "../apiConfig";
import "../Styles/ReportsPage.css";
import Pagination from "../Components/Common/Pagination";

const SuperadminReportPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyId, companyName } = location.state || {};

  const [totalUsers, setTotalUsers] = useState(0);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  const fetchCompanyUsers = useCallback(async () => {
    if (!companyId) {
      setError("Company ID is missing.");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/reports/company-users?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("idToken")}` },
        }
      );
       if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
      const data = await response.json();
      if (data.success) {
        setCompanyUsers(data.users);
        setFilteredUsers(data.users);
        setTotalUsers(data.users.length);
        setCurrentPage(1); 
      } else {
        setError(data.message || "Failed to fetch company users");
        console.error("Failed to fetch company users:", data.message);
      }
    } catch (error) {
      setError("An error occurred while fetching data");
      console.error("Error fetching company users:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    const token = localStorage.getItem('idToken');
    if (!token) {
      navigate('/');
      return;
    }
    if (companyId) {
      fetchCompanyUsers();
    } else {
      setError("No company selected. Please select a company first.");
    }
  }, [companyId, navigate, fetchCompanyUsers]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsers(companyUsers);
    } else {
      const filtered = companyUsers.filter(
        user => 
          user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, companyUsers]);

  const handleExportCSV = () => {
    const csvContent = [
      ["Name", "Email", "Employee ID", "Phone Number", "Status"],
      ...filteredUsers.map((user) => [
        user.name,
        user.email,
        user.employeeId,
        user.phoneNumber,
        user.isActive ? "Active" : "Inactive",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `users_report_${companyName || 'company'}.csv`;
    link.click();
  };
  
  // Get current users for pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="sr-container">
      <div className="sr-content">
        <div className="sr-header">
          <div className="sr-header-icon">
            <FileText size={28} color="#f3f4f6" />
          </div>
          <div className="sr-header-text">
            <h1>Company Reports</h1>
            {companyName && (
              <p className="sr-company-name">{companyName}</p>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="sr-stats-grid">
          <div className="sr-stat-card">
            <div className="sr-stat-icon">
              <Users size={24} color="#6366F1" />
            </div>
            <div className="sr-stat-content">
              <h3>Total Users</h3>
              <p>{totalUsers}</p>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="sr-filters-card">
          <div className="sr-filters-header">
            <h2>
              <Filter size={18} />
              Filter Reports
            </h2>
          </div>
          
          <div className="sr-filters-content">
            <div className="sr-date-filters">
              <div className="sr-filter-group">
                <label htmlFor="start-date">Start Date</label>
                <div className="sr-input-container">
                  <Calendar size={16} className="sr-input-icon" />
                  <input
                    type="date"
                    id="start-date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="sr-date-input"
                  />
                </div>
              </div>

              <div className="sr-filter-group">
                <label htmlFor="end-date">End Date</label>
                <div className="sr-input-container">
                  <Calendar size={16} className="sr-input-icon" />
                  <input
                    type="date"
                    id="end-date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="sr-date-input"
                  />
                </div>
              </div>

              <button 
                className="sr-filter-button" 
                onClick={fetchCompanyUsers}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader size={16} className="sr-spinner" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Filter size={16} />
                    Apply Filters
                  </>
                )}
              </button>
            </div>

            <div className="sr-search-container">
              <Search size={16} className="sr-search-icon" />
              <input
                type="text"
                placeholder="Search by name, email or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="sr-search-input"
              />
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="sr-results-card">
          <div className="sr-results-header">
            <h2>User Report</h2>
            {filteredUsers.length > 0 && (
              <button className="sr-export-button" onClick={handleExportCSV}>
                <Download size={16} />
                Export CSV
              </button>
            )}
          </div>

          {error ? (
            <div className="sr-error-container">
              <AlertCircle size={40} color="#ef4444" />
              <h3>Error</h3>
              <p>{error}</p>
              <button 
                className="sr-retry-button"
                onClick={fetchCompanyUsers}
              >
                <RefreshCw size={16} />
                Retry
              </button>
            </div>
          ) : loading ? (
            <div className="sr-loading-container">
              <Loader size={40} className="sr-loading-spinner" />
              <p>Loading user data...</p>
            </div>
          ) : (
            <div className="sr-table-container">
              {filteredUsers.length === 0 ? (
                <div className="sr-empty-state">
                  <Users size={40} color="#9ca3af" />
                  <h3>No Users Found</h3>
                  <p>
                    {companyUsers.length === 0 
                      ? "No user data available for the selected criteria." 
                      : "No users match your search criteria."}
                  </p>
                </div>
              ) : (
                <>
                  <table className="sr-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Employee ID</th>
                        <th>Phone Number</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.map((user) => (
                        <tr key={user.id} className="sr-table-row">
                          <td className="sr-name-cell">
                            <div className="sr-user-avatar">
                              {user.name?.charAt(0).toUpperCase() || "?"}
                            </div>
                            {user.name || "N/A"}
                          </td>
                          <td>{user.email || "N/A"}</td>
                          <td>{user.employeeId || "N/A"}</td>
                          <td>{user.phoneNumber || "N/A"}</td>
                          <td>
                            <span className={`sr-status-badge ${user.isActive ? "sr-status-active" : "sr-status-inactive"}`}>
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={paginate}
                      className="sr-pagination"
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperadminReportPage;
